import { useState, useEffect } from 'react';

type Theme = 'latte' | 'frappe' | 'macchiato' | 'mocha';

const themes = {
  latte: {
    name: 'Latte',
    colors: {
      base: '#eff1f5',
      mantle: '#e6e9ef',
      crust: '#dce0e8',
      text: '#4c4f69',
      subtext0: '#6c6f85',
      subtext1: '#5c5f77',
      surface0: '#ccd0da',
      surface1: '#bcc0cc',
      surface2: '#acb0be',
      overlay0: '#9ca0b0',
      overlay1: '#8c8fa1',
      overlay2: '#7c7f93',
      blue: '#1e66f5',
      lavender: '#7287fd',
      sapphire: '#209fb5',
      sky: '#04a5e5',
      teal: '#179299',
      green: '#40a02b',
      yellow: '#df8e1d',
      peach: '#fe640b',
      maroon: '#e64553',
      red: '#d20f39',
      mauve: '#8839ef',
      pink: '#ea76cb',
      flamingo: '#dd7878',
      rosewater: '#dc8a78'
    }
  },
  frappe: {
    name: 'Frappé',
    colors: {
      base: '#303446',
      mantle: '#292c3c',
      crust: '#232634',
      text: '#c6d0f5',
      subtext0: '#a5adce',
      subtext1: '#b5bfe2',
      surface0: '#414559',
      surface1: '#51576d',
      surface2: '#626880',
      overlay0: '#737994',
      overlay1: '#838ba7',
      overlay2: '#949cbb',
      blue: '#8caaee',
      lavender: '#babbf1',
      sapphire: '#85c1dc',
      sky: '#99d1db',
      teal: '#81c8be',
      green: '#a6d189',
      yellow: '#e5c890',
      peach: '#ef9f76',
      maroon: '#ea999c',
      red: '#e78284',
      mauve: '#ca9ee6',
      pink: '#f4b8e4',
      flamingo: '#eebebe',
      rosewater: '#f2d5cf'
    }
  },
  macchiato: {
    name: 'Macchiato',
    colors: {
      base: '#24273a',
      mantle: '#1e2030',
      crust: '#181926',
      text: '#cad3f5',
      subtext0: '#a5adcb',
      subtext1: '#b8c0e0',
      surface0: '#363a4f',
      surface1: '#494d64',
      surface2: '#5b6078',
      overlay0: '#6e738d',
      overlay1: '#8087a2',
      overlay2: '#939ab7',
      blue: '#8aadf4',
      lavender: '#b7bdf8',
      sapphire: '#7dc4e4',
      sky: '#91d7e3',
      teal: '#8bd5ca',
      green: '#a6da95',
      yellow: '#eed49f',
      peach: '#f5a97f',
      maroon: '#ee99a0',
      red: '#ed8796',
      mauve: '#c6a0f6',
      pink: '#f5bde6',
      flamingo: '#f0c6c6',
      rosewater: '#f4dbd6'
    }
  },
  mocha: {
    name: 'Mocha',
    colors: {
      base: '#1e1e2e',
      mantle: '#181825',
      crust: '#11111b',
      text: '#cdd6f4',
      subtext0: '#a6adc8',
      subtext1: '#bac2de',
      surface0: '#313244',
      surface1: '#45475a',
      surface2: '#585b70',
      overlay0: '#6c7086',
      overlay1: '#7f849c',
      overlay2: '#9399b2',
      blue: '#89b4fa',
      lavender: '#b4befe',
      sapphire: '#74c7ec',
      sky: '#89dceb',
      teal: '#94e2d5',
      green: '#a6e3a1',
      yellow: '#f9e2af',
      peach: '#fab387',
      maroon: '#eba0ac',
      red: '#f38ba8',
      mauve: '#cba6f7',
      pink: '#f5c2e7',
      flamingo: '#ffc6be',
      rosewater: '#f5e0dc'
    }
  }
};

// Global theme change event
const THEME_CHANGE_EVENT = 'themeChange';

export const useTheme = () => {
  const [currentTheme, setCurrentTheme] = useState<Theme>('latte');

  useEffect(() => {
    // First check localStorage for saved theme
    const savedTheme = localStorage.getItem('enki-theme') as Theme;
    
    // Then check user settings from localStorage
    const savedUser = localStorage.getItem('enki-user');
    let userTheme: Theme | null = null;
    
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        userTheme = parsedUser.settings?.theme;
      } catch (error) {
        console.error('Error parsing user theme:', error);
      }
    }
    
    // Use user theme if available, otherwise use saved theme, otherwise default to latte
    const themeToUse = userTheme || savedTheme || 'latte';
    
    if (themes[themeToUse]) {
      setCurrentTheme(themeToUse);
      applyThemeToDOM(themes[themeToUse].colors);
    }
  }, []);

  // Listen for global theme changes
  useEffect(() => {
    const handleThemeChange = (event: CustomEvent) => {
      const newTheme = event.detail.theme;
      setCurrentTheme(newTheme);
      applyThemeToDOM(themes[newTheme].colors);
    };

    window.addEventListener(THEME_CHANGE_EVENT as any, handleThemeChange);
    
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT as any, handleThemeChange);
    };
  }, []);

  const applyThemeToDOM = (themeColors: any) => {
    const root = document.documentElement;
    const body = document.body;
    
    // Apply theme colors to CSS variables
    Object.entries(themeColors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value as string);
    });
    
    // Apply to body and html for full coverage with transitions
    body.style.setProperty('background-color', themeColors.base, 'important');
    body.style.setProperty('color', themeColors.text, 'important');
    body.style.setProperty('transition', 'background-color 0.3s ease, color 0.3s ease', 'important');
    
    root.style.setProperty('background-color', themeColors.base, 'important');
    root.style.setProperty('color', themeColors.text, 'important');
    root.style.setProperty('transition', 'background-color 0.3s ease, color 0.3s ease', 'important');
    
    // Apply to root div
    const rootDiv = document.getElementById('root');
    if (rootDiv) {
      rootDiv.style.setProperty('background-color', themeColors.base, 'important');
      rootDiv.style.setProperty('color', themeColors.text, 'important');
      rootDiv.style.setProperty('transition', 'background-color 0.3s ease, color 0.3s ease', 'important');
      rootDiv.style.setProperty('min-height', '100vh', 'important');
    }
    
    // Force a repaint to ensure changes are applied immediately
    body.offsetHeight;
    root.offsetHeight;
  };

  const setTheme = (theme: Theme) => {
    setCurrentTheme(theme);
    
    // Apply theme immediately
    applyThemeToDOM(themes[theme].colors);
    
    // Save to localStorage immediately
    localStorage.setItem('enki-theme', theme);
    
    // Dispatch global theme change event
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, {
      detail: { theme }
    }));
  };

  return {
    currentTheme,
    setTheme,
    themes,
    colors: themes[currentTheme].colors
  };
};